import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
// Компоненты пользовательского интерфейса
import '../../styles/RailChainsTable.css';
import Modal from '../Modal/Modal';
import Input from '../CustomElements/Input';
import CustomButtonDelete from '../CustomElements/CustomButtonDelete';
import ConfirmationPage from '../CustomElements/ConfirmationPage';
import CustomContainer from '../CustomElements/CustomContainer';
// Утилиты
import { coordinateFormat, isCoordinateAllowed } from '../../utils/coordinateFormat';
import {
  handleFieldChange,
  parseCoordinatesString,
  validateCoordinate,
} from '../../utils/railChainsTableInputChangeHandlers';
import { trackChange } from '../../utils/trackChange';
// Действия Redux
import { load, remove } from '../../redux/actions/loader.action';
import { deleteRailChainsFromServer } from '../../redux/actions/infocards.action';

// Компонент для отображения таблицы редактирования рельсовых цепей

function RailChainsTable({
  source,
  copySource,
  setCopySource,
  handleModify,
  errorIds,
  stationId,
  setErrorIds,
  setErrorCoordIds,
  errorCoordIds,
  isUpdate,
  successModal,
  setSuccessModal,
  confirmDelete,
  setConfirmDelete,
  modifiedRailChains,
  t,
}) {
  const dispatch = useDispatch();

  // Получаем данные о процессе
  let loader = useSelector((state) => state.loader);

  // Состояния для отображения компонентов и модального окна
  const [isOpen, setIsOpen] = useState(false);

  // состояние для записи id рц который планируют удалить
  const [idRcForDelete, setIdRcForDelete] = useState();

  // Открыть модальное окно
  const openModal = () => {
    setIsOpen(true);
  };

  // Закрыть модальное окно
  const closeModal = () => {
    setIsOpen(false);
  };

  // функция для удаления РЦ
  const deleteRailChains = () => {
    dispatch(load());
    dispatch(deleteRailChainsFromServer(stationId, idRcForDelete));
  };

  // отслеживаем выполнение запроса
  useEffect(() => {
    if (!loader?.isLoading && loader?.isSuccess && confirmDelete) {
      setConfirmDelete(false);
      setSuccessModal(true);
      // очищаем стейт отслеживающий загрузку
      dispatch(remove());
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loader?.isLoading, loader?.isSuccess]);

  /**
   * Обновляет список координатных ошибок для конкретного поля (широта/долгота).
   *
   * @param {Event} event - Событие blur с input-а
   * @param {Object} row - Строка таблицы, содержащая координаты
   * @param {number} index - Индекс строки в массиве
   * @param {string} fieldName - Название поля (например, 'coordinateEndLat')
   */
  const updateErrorCoordIds = useCallback(
    (event, row, index, fieldName) => {
      // Определяем тип координаты: широта или долгота
      const isLatitude = fieldName.includes('Lat');
      // Проверяем валидность координаты
      const isValid = validateCoordinate(event?.target?.value, isLatitude);

      setErrorCoordIds((prevErrorCoordIds) => {
        const updatedErrorCoordIds = new Set(prevErrorCoordIds);

        // Формируем ключ для ошибки
        const errorKey = `${row?.id}-${fieldName}-${index}`;

        if (isValid) {
          // Удаляем из Set ошибку, если она была
          updatedErrorCoordIds.delete(errorKey);
        } else {
          // Добавляем в Set ошибку, если валидация не прошла
          updatedErrorCoordIds.add(errorKey);
        }

        return updatedErrorCoordIds;
      });
    },
    [setErrorCoordIds]
  );

  /**
   * Обновляет список `errorIds`, валидируя координаты из поля `track`.
   * Если координаты невалидны — добавляет их ID в список ошибок.
   * Если координаты валидны — удаляет ID из ошибок и обновляет значение поля.
   *
   * Обернута в useCallback для мемоизации.
   *
   * @param {Object} event - Событие blur из поля ввода
   * @param {Object} row - Текущая строка данных
   * @param {number} index - Индекс строки
   */
  const updateErrorIdsAndHandleFieldChange = useCallback(
    (event, row, index) => {
      // Парсинг строки координат и получение невалидных id и массива координат
      const { invalidCoordinatesIds, coordinatesArray } = parseCoordinatesString(event?.target?.value, row?.id);

      if (invalidCoordinatesIds?.length > 0) {
        // Если есть невалидные id, добавляем их в список ошибок
        setErrorIds((prevErrorIds) => {
          const uniqueErrorIds = new Set([...prevErrorIds, ...invalidCoordinatesIds]);
          return Array.from(uniqueErrorIds);
        });
      } else {
        // Если проверка прошла успешно, удаляем id из списка ошибок
        setErrorIds((prevErrorIds) => {
          const index = prevErrorIds.indexOf(row?.id);
          if (index !== -1) {
            const updatedErrorIds = [...prevErrorIds];
            updatedErrorIds.splice(index, 1);
            return updatedErrorIds;
          }
          return prevErrorIds;
        });
        // Обработка изменения поля после успешной валидации
        handleFieldChange(event, index, 'track', setCopySource, false, coordinatesArray);
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [setErrorIds]
  );

  /**
   * Удаляет указанный ID из массива ошибок `errorIds`, если он там присутствует.
   * Используется для очистки ошибки координат при фокусе на поле или валидации.
   *
   * Обернута в useCallback для предотвращения лишних перерендеров.
   *
   * @param {string | number} id - ID элемента, который нужно удалить из списка ошибок
   */
  const removeErrorId = useCallback((id) => {
    setErrorIds((prevErrorIds) => {
      // Находим индекс id в текущем массиве ошибок
      const index = prevErrorIds.indexOf(id);
      if (index !== -1) {
        // Если id найден в массиве, создаем его копию
        const updatedErrorIds = [...prevErrorIds];
        // Удаляем id из копии массива
        updatedErrorIds.splice(index, 1);
        // Возвращаем обновленный массив без удаленного id
        return updatedErrorIds;
      }
      // Возвращаем текущий массив, если id не найден
      return prevErrorIds;
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  /**
   * Обрабатывает уход с инпута: обновляет значение поля, проверяет координаты или трек,
   * и отслеживает изменения для последующей отправки.
   *
   * @param {Object} params
   * @param {Event} params.event - Событие blur от инпута
   * @param {number} params.index - Индекс строки в таблице
   * @param {Object} params.row - Объект строки (РЦ)
   * @param {string | number} params.id - ID РЦ
   * @param {string} params.fieldName - Название изменяемого поля
   * @param {*} params.oldValue - Старое значение поля до изменений
   * @param {string} params.nameRC - Название РЦ (для трекинга)
   * @param {boolean} [params.isTrackField=false] - Флаг, указывающий, что поле — трек
   * @param {boolean} [params.useUpdateErrorCoordIds=false] - Флаг, активирующий проверку координат на ошибки
   */
  const handleInputBlur = ({
    event,
    index,
    row,
    id,
    fieldName,
    oldValue,
    nameRC,
    isTrackField = false,
    useUpdateErrorCoordIds = false,
  }) => {
    // Если поле — "трек", обрабатываем отдельно с парсингом координат и валидацией
    if (isTrackField) {
      updateErrorIdsAndHandleFieldChange(event, row, index);
    } else {
      // Иначе просто обновляем значение поля
      handleFieldChange(event, index, fieldName, setCopySource, false);
    }

    // Если нужно — проверяем координаты на корректность и обновляем ошибки
    if (useUpdateErrorCoordIds) {
      updateErrorCoordIds(event, row, index, fieldName);
    }

    // Трекаем изменение для сохранения
    trackChange(modifiedRailChains, id, fieldName, oldValue, event?.target?.value, nameRC, handleModify);
  };

  /**
   * Возвращает сообщение об ошибке для координатного поля
   * @param {Object} row — строка таблицы
   * @param {string} key — имя текущего поля (например, coordinateEndLat)
   * @param {string} inputId — уникальный ID инпута
   * @param {Set<string>} errorCoordIds — Set с ошибками координат
   * @returns {string} сообщение об ошибке или пустая строка
   */
  function getCoordinateMessageError(row, key, inputId, errorCoordIds) {
    const currentValue = row?.[key];
    const isLatField = key.includes('Lat');
    const pairKey = key.includes('Lat') ? key.replace('Lat', 'Lon') : key.replace('Lon', 'Lat');
    const pairValue = row?.[pairKey];

    const hasFormatError = errorCoordIds.has(inputId);

    // Если поле есть в списке ошибок и введено значение — это ошибка формата
    if (hasFormatError && currentValue) {
      return t?.invalidFormat || 'неверный формат';
    }

    // Если ТЕКУЩЕЕ поле пустое, а ПАРНОЕ заполнено — ошибка в этом поле
    if (!currentValue && pairValue) {
      return isLatField ? t?.enterLatitude || 'введите широту' : t?.enterLongitude || 'введите долготу';
    }

    return '';
  }

  //пропсы для инпутов
  const inputProps = {
    disableHover: true,
    textAlign: 'center',
    background: 'white',
    margin: '0px',
    width: '100%',
    height: '100%',
    mask: '9999',
  };

  // пропсы для инпута координат
  const inputCoordProps = {
    isCoordinate: true, // Устанавливаем, что это координатный ввод
    format: coordinateFormat, // Передаем кастомный формат
    isAllowed: isCoordinateAllowed, // Передаем кастомную валидацию
    width: '100%',
    height: '100%',
    disableHover: true,
    textAlign: 'center',
    background: 'white',
    margin: '0px',
    isNotMarginMessage: true,
  };

  // пропсы для инпута ординат
  const inputOrdinatProps = {
    disableHover: true,
    textAlign: 'center',
    background: 'white',
    margin: '0px',
    width: '100%',
    height: '100%',
    format: '#######',
    maxLength: 8,
    isCoordinate: true,
    isAllowed: undefined,
  };

  // пропсы для инпута трека
  const inputTrackProps = {
    restrictionRegex: /^[0-9.,;\s-]*$/,
    name: 'track',
    width: '100%',
    height: '100%',
    disableHover: true,
    textAlign: 'center',
    background: 'white',
    margin: '0px',
    isNotMarginMessage: true,
    internalProcessing: true,
  };

  // поля для инпутов
  // key - имя поля в объекте
  // props - пропсы для инпутов
  // coord - флаг для определения координатного инпута
  const fields = [
    { key: 'alertLengthsPCh', props: inputOrdinatProps },
    { key: 'alertLengthsECh', props: inputOrdinatProps },
    { key: 'object', props: { ...inputProps, mask: undefined, internalProcessing: true } },
    { key: 'railChainName', props: { ...inputProps, mask: undefined, internalProcessing: true } },
    { key: 'ordEnd', props: inputOrdinatProps },
    { key: 'controlledOrdA', props: inputOrdinatProps },
    { key: 'controlledOrdB', props: inputOrdinatProps },
    { key: 'controlledOrdV', props: inputOrdinatProps },
    { key: 'controlledOrdG', props: inputOrdinatProps },
    { key: 'uncontrolledOrdN1', props: inputOrdinatProps },
    { key: 'uncontrolledOrdN2', props: inputOrdinatProps },
    { key: 'uncontrolledOrdN3', props: inputOrdinatProps },
    { key: 'coordinateEndLat', props: inputCoordProps, coord: true },
    { key: 'coordinateEndLon', props: inputCoordProps, coord: true },
    { key: 'coordinateControlledLatA', props: inputCoordProps, coord: true },
    { key: 'coordinateControlledLonA', props: inputCoordProps, coord: true },
    { key: 'coordinateControlledLatB', props: inputCoordProps, coord: true },
    { key: 'coordinateControlledLonB', props: inputCoordProps, coord: true },
    { key: 'coordinateControlledLatV', props: inputCoordProps, coord: true },
    { key: 'coordinateControlledLonV', props: inputCoordProps, coord: true },
    { key: 'coordinateControlledLatG', props: inputCoordProps, coord: true },
    { key: 'coordinateControlledLonG', props: inputCoordProps, coord: true },
    { key: 'coordinateUncontrolledLatN1', props: inputCoordProps, coord: true },
    { key: 'coordinateUncontrolledLonN1', props: inputCoordProps, coord: true },
    { key: 'coordinateUncontrolledLatN2', props: inputCoordProps, coord: true },
    { key: 'coordinateUncontrolledLonN2', props: inputCoordProps, coord: true },
    { key: 'coordinateUncontrolledLatN3', props: inputCoordProps, coord: true },
    { key: 'coordinateUncontrolledLonN3', props: inputCoordProps, coord: true },
  ];

  // Хедеры для координат
  const coordHeaderLabels = useMemo(() => ['A', 'Б', 'В', 'Г', 'Н1', 'Н2', 'Н3'], []);

  // текст для тултипа с информацией о вводе координат
  const coordInfoFormat = useMemo(() => t?.coordInfoFormat, [t]);

  // Хедер таблицы
  const tableHeaderConfig = useMemo(
    () => [
      { label: t?.railChId || 'ID РЦ', rowSpan: 4, className: 'sticky-col top-border left-border' },
      { label: t?.alertSectionLength || 'Длина участка оповещения', colSpan: 2, rowSpan: 2, className: 'top-border' },
      { label: t?.object || 'Объект', rowSpan: 4, className: 'top-border' },
      { label: t?.rc || 'РЦ', rowSpan: 4, className: 'top-border' },
      { label: t?.ordinalsEnd || 'Ординаты концов РЦ', colSpan: 8, className: 'top-border' },
      {
        label: t?.coordinatesEnd || 'Координаты концов РЦ',
        colSpan: 16,
        className: 'sub-header-tooltip top-border coord',
        tooltip: coordInfoFormat,
      },
      {
        label: t?.track || 'Трек',
        rowSpan: 4,
        className: 'sub-header-tooltip top-border',
        tooltip: t?.tooltip || 'Формат: широта, долгота; широта, долгота; . . . ;',
        isTrack: true,
      },
      { label: t?.deleteRailChain || 'Удаление РЦ', rowSpan: 4, className: 'top-border cell-delete' },
      { label: t?.ordEndHeader || 'Питающий конец', rowSpan: 3 },
      { label: t?.controlledBranch || 'Контролируемое ответвление', colSpan: 4 },
      { label: t?.uncontrolledBranch || 'Неконтролируемое ответвление', colSpan: 3 },
      { label: t?.ordEndHeader || 'Питающий конец', colSpan: 2, rowSpan: 2 },
      { label: t?.controlledBranch || 'Контролируемое ответвление', colSpan: 8 },
      { label: t?.uncontrolledBranch || 'Неконтролируемое ответвление', colSpan: 6 },
      { label: t?.labelPCh || 'ПЧ', rowSpan: 2 },
      { label: t?.labelECh || 'ЭЧ', rowSpan: 2 },
    ],
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [t]
  );

  return (
    <div className="table-container">
      {/* хедер таблицы на гридах */}
      <div className="table-header">
        {tableHeaderConfig.map(({ label, className = '', colSpan, rowSpan, tooltip, isTrack }, i) => (
          <div
            key={i}
            className={`sub-header header-center ${className}`}
            col-span={colSpan && colSpan}
            row-span={rowSpan && rowSpan}
          >
            {/* если есть тултип, то отрисовываем отдельно для трека и Координаты концов РЦ */}
            {tooltip ? (
              <>
                {label}
                <div className={`info-icon${isTrack ? '-track' : ''}`}>
                  <div className="tooltip">{tooltip}</div>
                </div>
              </>
            ) : (
              label
            )}
          </div>
        ))}
        {/* отрисовываем хедеры Ординаты */}
        {coordHeaderLabels.map((label) => (
          <div key={`ord-${label}`} className="sub-header header-center" row-span={2}>
            {label}
          </div>
        ))}
        {/* отрисовываем хедеры Координаты  */}
        {coordHeaderLabels.map((label) => (
          <div key={`coord-${label}`} className="sub-header header-center" col-span={2}>
            {label}
          </div>
        ))}

        {/* отрисовываем хедеры широта и долгота для всех координат */}
        {Array.from({ length: 8 }).map((_, i) => (
          <React.Fragment key={i}>
            <div className="sub-header header-center">{t?.latitude || 'Широта'}</div>
            <div className="sub-header header-center">{t?.longitude || 'Долгота'}</div>
          </React.Fragment>
        ))}
      </div>

      {/* данные таблица */}
      <div className="table-body">
        {copySource.map((row, index) => {
          const oldRailCain = source?.find((railCain) => railCain?.id === row.id);
          const id = row?.id;
          const nameRC = oldRailCain?.railChainName;
          const oldTrack = oldRailCain?.track?.map(({ lat, lon }) => lat + ',' + lon)?.join(';');
          const currentTrack = row?.track?.map(({ lat, lon }) => lat + ',' + lon)?.join(';') || '';

          return (
            <div key={index} className="table-row">
              <div className="cell sticky-col left-border">{id}</div>

              {fields.map(({ key, props, coord }) => {
                //создаем id  инпута
                const inputId = `${id}-${key}-${index}`;
                //получаем ошибку если есть
                const messageError = coord ? getCoordinateMessageError(row, key, inputId, errorCoordIds) : '';

                return (
                  <div className="cell" key={key}>
                    <Input
                      id={inputId}
                      value={row?.[key] || ''}
                      onChange={(event) => isUpdate(id)}
                      onBlur={(event) => {
                        handleInputBlur({
                          event,
                          index,
                          row,
                          id,
                          fieldName: key,
                          oldValue: oldRailCain?.[key],
                          nameRC,
                          isTrackField: key === 'track',
                          useUpdateErrorCoordIds: coord,
                        });
                      }}
                      messageError={messageError}
                      {...props}
                    />
                  </div>
                );
              })}
              {/* отдельно отрисовываем инпут для трека */}
              <div className="cell">
                <Input
                  id={`${id}-track-${index}`}
                  value={currentTrack}
                  onChange={(event) => isUpdate(id)}
                  onBlur={(event) => {
                    handleInputBlur({
                      event,
                      index,
                      row,
                      id,
                      fieldName: 'track',
                      oldValue: oldTrack,
                      nameRC,
                      isTrackField: true,
                      useUpdateErrorCoordIds: false,
                    });
                  }}
                  {...inputTrackProps}
                  onFocus={() => removeErrorId(id)}
                  messageError={
                    errorIds.includes(id) ? t?.trackError || 'Координаты в поле трек введены некорректно' : ''
                  }
                />
              </div>
              {/* кнопка для удаления рц */}
              <div className="cell cell-delete">
                <CustomButtonDelete
                  onClick={() => {
                    setConfirmDelete(true);
                    openModal();
                    setIdRcForDelete(id);
                  }}
                />
              </div>
            </div>
          );
        })}
      </div>
      {/* Модальное окно */}
      <Modal width="50%" height="60%" isOpen={isOpen} onClose={closeModal}>
        {/* подтверждение удаления РЦ  */}
        {confirmDelete && (
          <CustomContainer>
            <ConfirmationPage
              onConfirm={deleteRailChains}
              onCancel={closeModal}
              confirmButtonText={t?.confirm || 'Подтвердить'}
              cancelButtonText={t?.cancel || 'Отмена'}
              confirmText={
                t?.confirmDeleteRc?.replace('{{id}}', idRcForDelete) ||
                `Вы уверены, что хотите удалить РЦ ${idRcForDelete}?`
              }
              btnWidth="var(--sadr-font-size-280)"
            />
          </CustomContainer>
        )}
        {/* при успешном удалении РЦ */}
        {successModal && (
          <CustomContainer>
            <ConfirmationPage
              onConfirm={() => {
                setSuccessModal(false);
                closeModal();
              }}
              confirmText={
                t?.deleteRcSuccess?.replace('{{id}}', idRcForDelete) || `РЦ id ${idRcForDelete} успешно удалено!`
              }
              addNew={t?.close || 'Закрыть'}
              btnWidth="var(--sadr-font-size-280)"
            />
          </CustomContainer>
        )}
      </Modal>
    </div>
  );
}

export default RailChainsTable;
